import React, { useMemo, useRef, useContext, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import PieChart, {
    Series,
    Label,
    Connector,
    Legend,
    Tooltip,
    Export,
    Title,
    Font,
} from 'devextreme-react/pie-chart';
import Chart, { Label as ChartLabel, Legend as ChartLegend, Series as ChartSeries, Tooltip as ChartTooltip, ValueAxis } from 'devextreme-react/chart';
import { useRaceData } from '../contexts';
import { useDashboardChartView } from '../contexts/DashboardChartViewContext';
import { RaceFilterControls } from './RaceFilterControls';
import { DashboardCard, DashboardCardContext } from '../components/DashboardCard';
import { DashboardChartViewSelect } from '../components/DashboardChartViewSelect';
import { DASHBOARD_CHART_VIEW_CONFIG, DASHBOARD_DISTRIBUTION_COLORS, DashboardChartView, getDashboardDistributionColor } from '../types';

interface AggregatedRace {
    raceName: string;
    studentCount: number;
    percentage: number;
}

const RaceChartContent = ({ chartRef, cartesianChartRef, chartView, setChartView }: { chartRef: React.RefObject<PieChart>; cartesianChartRef: React.RefObject<Chart>; chartView: DashboardChartView; setChartView: (view: DashboardChartView) => void }) => {
    const { data, loading, error, grandTotal, filter } = useRaceData();
    const { isExpanded } = useContext(DashboardCardContext);

    const styleConfig = isExpanded ? {
        centerTitleSize: 42,
        centerSubSize: 18,
        innerRadius: 0.68,
        labelSize: 14
    } : {
        centerTitleSize: 32,
        centerSubSize: 14,
        innerRadius: 0.65,
        labelSize: 12
    };

    // Prepare metadata for export
    const exportMetadata = useMemo(() => {
        const yearsLabel = filter.schoolYears.length > 0
            ? filter.schoolYears.map(y => `${y}-${y + 1}`).join(', ')
            : 'All Years';
        
        return {
            title: 'Race Distribution',
            subtitle: `Years: ${yearsLabel}`
        };
    }, [filter.schoolYears]);

    // Aggregate data across all selected years
    const aggregatedData = useMemo((): AggregatedRace[] => {
        if (!data || data.length === 0) return [];

        const raceMap = new Map<string, { count: number; displayOrder: number }>();

        data.forEach(item => {
            const existing = raceMap.get(item.raceName);
            if (existing) {
                existing.count += item.studentCount;
            } else {
                raceMap.set(item.raceName, {
                    count: item.studentCount,
                    displayOrder: item.raceDisplayOrder
                });
            }
        });

        const total = grandTotal?.totalStudents || 0;

        return Array.from(raceMap.entries())
            .map(([raceName, { count, displayOrder }]) => ({
                raceName,
                studentCount: count,
                percentage: total > 0 ? Math.round((count / total) * 100 * 10) / 10 : 0,
                displayOrder
            }))
            .sort((a, b) => a.displayOrder - b.displayOrder);
    }, [data, grandTotal]);

    const totalStudents = grandTotal?.totalStudents || 0;

    if (loading) {
        return <Skeleton variant="rounded" height={400} />;
    }

    if (error) {
        return (
            <Typography color="error" align="center">
                {error}
            </Typography>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Typography align="center" color="textSecondary">
                No race data available for selected years
            </Typography>
        );
    }

    const formatPercentage = (percent: number): string => {
        const pct = percent * 100;
        if (pct > 0 && pct < 1) return '<1';
        return Math.round(pct).toString();
    };

    const centerTemplate = () => {
        return (
            <svg>
                <text 
                    textAnchor="middle" 
                    x="50%" 
                    y="45%" 
                    style={{ 
                        fontSize: `${styleConfig.centerTitleSize}px`, 
                        fontWeight: 'bold',
                        fill: '#000'
                    }}
                >
                    {totalStudents.toLocaleString()}
                </text>
                <text 
                    textAnchor="middle" 
                    x="50%" 
                    y="55%" 
                    style={{ 
                        fontSize: `${styleConfig.centerSubSize}px`,
                        fill: '#666'
                    }}
                >
                    Students
                </text>
            </svg>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}><RaceFilterControls /><DashboardChartViewSelect dashboard="race" value={chartView} onChange={setChartView} /></Box>
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                {(chartView === 'pie' || chartView === 'fullPie') ? <PieChart
                    ref={chartRef}
                    dataSource={aggregatedData}
                    type={chartView === 'fullPie' ? 'pie' : 'doughnut'}
                    innerRadius={styleConfig.innerRadius}
                    height="100%"
                    palette={DASHBOARD_DISTRIBUTION_COLORS}
                    centerRender={centerTemplate}
                >
                    <Title text={exportMetadata.title} subtitle={exportMetadata.subtitle}>
                        <Font size={18} weight={600} />
                    </Title>
                    <Series
                        argumentField="raceName"
                        valueField="studentCount"
                    >
                        <Label
                            visible={true}
                            position="outside"
                            customizeText={(pointInfo: { argumentText: string; valueText: string; percent: number }) => {
                                const displayPct = formatPercentage(pointInfo.percent);
                                return `${pointInfo.argumentText}\n${pointInfo.valueText} (${displayPct}%)`;
                            }}
                        >
                            <Font size={styleConfig.labelSize} />
                            <Connector visible={true} width={1} />
                        </Label>
                    </Series>
                    <Legend visible={false} />
                    <Tooltip
                        enabled={true}
                        customizeTooltip={(pointInfo: { argumentText: string; valueText: string; percent: number }) => {
                            const displayPct = formatPercentage(pointInfo.percent);
                            return {
                                text: `${pointInfo.argumentText}: ${pointInfo.valueText} (${displayPct}%)`
                            };
                        }}
                    />
                    <Export enabled={isExpanded} />
                </PieChart> : <Chart ref={cartesianChartRef} dataSource={aggregatedData} height="100%" rotated={chartView === 'horizontalBar'} palette={DASHBOARD_DISTRIBUTION_COLORS} customizePoint={(args: any) => ({ color: getDashboardDistributionColor(args.index) })}><ChartSeries type="bar" argumentField="raceName" valueField="studentCount"><ChartLabel visible /></ChartSeries><ValueAxis allowDecimals={false} /><ChartLegend visible={false} /><ChartTooltip enabled /></Chart>}
            </Box>
        </Box>
    );
};

export const RaceChartCard = () => {
    const { filter } = useRaceData();
    const chartRef = useRef<PieChart>(null);
    const cartesianChartRef = useRef<Chart>(null);
    const [chartView, setChartView] = useDashboardChartView('race');

    const handleExport = () => {
        if ((chartView === 'pie' || chartView === 'fullPie') && chartRef.current) {
            const yearsLabel = filter.schoolYears.length > 0
                ? filter.schoolYears.join('_')
                : 'AllYears';
            chartRef.current.instance.exportTo(`Race_${yearsLabel}`, 'png');
        } else cartesianChartRef.current?.instance.exportTo(`Race_${filter.schoolYears.join('_') || 'AllYears'}`, 'png');
    };

    return (
        <DashboardCard title="Race" onExport={handleExport}>
            <RaceChartContent chartRef={chartRef} cartesianChartRef={cartesianChartRef} chartView={chartView} setChartView={setChartView} />
        </DashboardCard>
    );
};
